import React, { useEffect, useState, useRef } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { Form, Button, Container, Row, Col, Card, Alert, Spinner, Modal } from "react-bootstrap";
import { Input } from "reactstrap";
import SimpleHeader from "components/Headers/SimpleHeader";
import { deviceForm, extensionForm, extensionSettingsForm, gatewayForm, userForm } from "./configs/Forms/AccountsForms";
import { destinationsForm, dialplanManagerForm } from "./configs/Forms/DialplanForms";
import { callBlockForm } from "./configs/Forms/ApplicationsForms";
import { domainForm, domainSettingsForm, defaultSettingsForm, modulesForm, varsForm, sofiaGlobalSettingsForm, databasesForm } from "./configs/Forms/AdvancedForms";
import { resolveSingularName } from "./configs/Naming";
import { serverHost } from "variables/serverdata";
import "react-quill/dist/quill.snow.css";

const formConfigs = {
  v_extensions: extensionForm,
  v_extension_settings: extensionSettingsForm,
  v_gateways: gatewayForm,
  v_users: userForm,
  v_destinations: destinationsForm,
  v_dialplans: dialplanManagerForm,
  v_call_block: callBlockForm,
  v_domains: domainForm,
  v_domain_settings: domainSettingsForm,
  v_default_settings: defaultSettingsForm,
  v_modules: modulesForm,
  v_vars: varsForm,
  v_sofia_global_settings: sofiaGlobalSettingsForm,
  v_databases: databasesForm
};

const pkFields = {
  v_extensions: "extension_uuid",
  v_extension_settings: "extension_setting_uuid",
  v_gateways: "gateway_uuid",
  v_users: "user_uuid",
  v_destinations: "destination_uuid",
  v_dialplans: "dialplan_uuid",
  v_call_block: "call_block_uuid",
  v_domains: "domain_uuid",
  v_domain_settings: "domain_setting_uuid",
  v_default_settings: "default_setting_uuid",
  v_modules: "module_uuid",
  v_vars: "var_uuid",
  v_sofia_global_settings: "sofia_global_setting_uuid",
  v_databases: "database_uuid"
};

const tableRoutes = {
  v_extensions: "extensions",
  v_extension_settings: "extensionSettings",
  v_gateways: "gateways",
  v_devices: "devices",
  v_users: "users",
  v_destinations: "destinations",
  v_dialplans: "dialplans",
  v_call_block: "callBlock",
  v_domains: "domains",
  v_domain_settings: "domain-settings",
  v_default_settings: "default-settings",
  v_modules: "modules",
  v_vars: "variables",
  v_sofia_global_settings: "sofiaGlobalSettings",
  v_databases: "databases"
};

const titleCase = (s) =>
  s.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

const GenericEdit = () => {
  const { table: tableName, id } = useParams();
  const navigate = useNavigate();
  const formConfig = formConfigs[tableName];
  const pkField = pkFields[tableName];
  const routePath = tableRoutes[tableName] || tableName.replace("v_", "");

  const [rowData, setRowData] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [dynamicOptions, setDynamicOptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [missingFields, setMissingFields] = useState([]);
  const [error, setError] = useState("");

  const [showAdvancedModal, setShowAdvancedModal] = useState(false);
  const [advancedData, setAdvancedData] = useState({});
  const advancedInitial = useRef({});

  const singular = resolveSingularName(null, tableName);
  const location = useLocation();
  const errorRef = useRef(null);


  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [error]);

  // Fetch row data
  useEffect(() => {
    if (!formConfig || !pkField) {
      setError("Invalid table");
      setLoading(false);
      return;
    }

    const fetchRow = async () => {
      try {
        const res = await fetch(
          `${serverHost}/get_row?table=${tableName}&pk=${pkField}&id=${encodeURIComponent(id)}`
        );
        if (!res.ok) throw new Error(res.status === 404 ? "Row not found" : "Failed to fetch row");
        const data = await res.json();
        if (!data?.result) throw new Error("Row not found");

        let row = data.result;

        // --- SOFIA GLOBAL SETTINGS KEY REMAP ---
        if (tableName === "v_sofia_global_settings") {
          const mapped = {};
          Object.entries(row).forEach(([key, val]) => {
            if (key.startsWith("global_setting_")) {
              const newKey = key.replace("global_setting_", "sofia_global_setting_");
              mapped[newKey] = val;
            } else {
              mapped[key] = val;
            }
          });
          row = mapped;
        }

        // --- EXTENSION SETTINGS SPECIAL CASE ---
        if (tableName === "v_extension_settings" && row.extension_uuid) {
          try {
            const extRes = await fetch(
              `${serverHost}/get_row?table=v_extensions&pk=extension_uuid&id=${encodeURIComponent(row.extension_uuid)}`
            );
            const extData = await extRes.json();
            row.extension = extData.result?.extension || "";
          } catch (err) {
            console.warn("Failed to fetch extension number:", err);
          }
        }

        setRowData(row);
        setOriginalData(row);

        // Initialize advancedData
        const adv = {};
        Object.entries(formConfig.sections || {})
          .filter(([_, section]) => typeof section === "object" && section.advanced)
          .forEach(([_, section]) => {
            section.fields.forEach((f) => {
              adv[f] = row[f] ?? "";
            });
          });
        setAdvancedData(adv);
        advancedInitial.current = adv;

      } catch (err) {
        console.error("Error fetching row:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRow();
  }, [tableName, pkField, id, formConfig]);

  // Fetch dynamic FK options 
  useEffect(() => {
    const fetchOptions = async () => {
      setLoadingOptions(true);
      try {
        const res = await fetch(`${serverHost}/get_options?table=${tableName}`);
        const data = await res.json();
        const formatted = {};

        // Standard options from backend
        Object.entries(data.options || {}).forEach(([field, opts]) => {
          formatted[field] = opts.map((opt) => typeof opt === "object" ? opt : { label: opt, value: opt });
        });
        setDynamicOptions(formatted);
      } catch (err) {
        console.error("Error fetching options:", err);
      } finally {
        setLoadingOptions(false);
      }
    };
    fetchOptions();
  }, [tableName]);

  // Map FK UUIDs to labels
  useEffect(() => {
    if (!rowData || !dynamicOptions) return;
    if (formConfig.foreignKeyMapping) {
      const mappedData = { ...rowData };
      Object.entries(formConfig.foreignKeyMapping).forEach(([field, fk]) => {
        const options = dynamicOptions[field] || [];
        const match = options.find((opt) => opt.value === mappedData[fk.valueColumn]);
        if (match) mappedData[field] = match.label;
      });
      setRowData(mappedData);
    }
  }, [rowData, dynamicOptions, formConfig]);

  const handleChange = (field, value) => setRowData(prev => ({ ...prev, [field]: value }));
  const handleAdvancedFieldChange = (field, value) => setAdvancedData(prev => ({ ...prev, [field]: value }));

  const isFieldDirty = (current, original) => {
    const normalize = (val) => (val == null ? "" : String(val));
    return normalize(current) !== normalize(original);
  };

  const isDirty =
    Object.keys(rowData || {}).some(
      (key) => isFieldDirty(rowData[key], originalData?.[key])
    ) ||
    Object.keys(advancedData || {}).some(
      (key) => isFieldDirty(advancedData[key], advancedInitial.current?.[key])
    );

  const handleSave = async () => {
    setError("");
    const missing = [];
    const user = JSON.parse(localStorage.getItem("user"));
    const update_user = user?.user_uuid || null;

    // Merge advancedData into rowData before validating
    const payloadData = { ...rowData, ...advancedData };

    (formConfig.required || []).forEach((field) => {
      if (!payloadData[field] || payloadData[field] === "") missing.push(field);
    });

    Object.entries(formConfig.sections || {}).forEach(([sectionName, section]) => {
      if (section.advanced && section.requiredFields) {
        section.fields.forEach((f) => {
          if (section.requiredFields.includes(f) && (!payloadData[f] || payloadData[f] === "")) {
            missing.push(f);
          }
        });
      }
    });

    if (missing.length > 0) {
      setMissingFields(missing); // highlight missing fields
      setError(
        `Required fields missing: ${missing
          .map((f) => formConfig.labels?.[f] || f)
          .join(", ")}`
      );
      return;
    }

    setMissingFields([]);

    const finalData =
      tableName === "v_sofia_global_settings"
        ? Object.fromEntries(
          Object.entries(payloadData).map(([k, v]) =>
            k.startsWith("sofia_global_setting_")
              ? [k.replace("sofia_global_setting_", "global_setting_"), v]
              : [k, v]
          )
        )
        : payloadData;

    try {
      const payload = {
        table: tableName,
        data: { ...finalData, update_user },
        where: { [pkFields[tableName]]: rowData[pkFields[tableName]] },
        formConfig,
      };

      const res = await fetch(`${serverHost}/update`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed to update record");

      let openSection = null;
      if (formConfig.groupBy) {
        openSection = payloadData[formConfig.groupBy];
      }
      navigate(
        `/admin/hucs/${tableRoutes[tableName] || tableName.replace("v_", "")}`,
        { state: { openSection } }
      );

    } catch (err) {
      console.error("Error saving row:", err);
      setError(err.message || "Failed to save record");
    }
  };

  const getAllFieldsWithoutSections = () => {
    const config = formConfig || {};
    const labels = config.labels || {};

    // Get all unique field names from labels, required, defaults
    const allFields = new Set([
      ...Object.keys(labels || {}),
      ...(config.required || []),
      ...(config.defaults ? Object.keys(config.defaults) : []),
    ]);

    return Array.from(allFields);
  };


  const handleBack = () => {
    if (isDirty && !window.confirm("Discard changes and go back?")) return;
    navigate(`/admin/hucs/${routePath}`);
  };

  const handleAdvancedBack = () => {
    if (isDirty && !window.confirm("Discard changes in Advanced Options?")) return;

    setShowAdvancedModal(false);
    setAdvancedData({ ...advancedInitial.current });
  };

  const handleAdvancedSave = () => {
    // Merge advancedData into rowData
    setRowData((prev) => ({ ...prev, ...advancedData }));

    // Update baseline for advanced fields
    advancedInitial.current = { ...advancedData };

    setShowAdvancedModal(false);
  };

  const hasSections =
    formConfig?.sections && Object.keys(formConfig.sections).length > 0;

  const advancedSections = hasSections
    ? Object.entries(formConfig.sections).filter(
      ([_, section]) => typeof section === "object" && section.advanced
    )
    : [];

  const basicSections = hasSections
    ? Object.entries(formConfig.sections).filter(
      ([_, section]) => !section.advanced
    )
    : [];

  const renderField = (tableConfig, field, value, onChange) => {
    const type = tableConfig.types?.[field] || "text";
    const label =
      tableConfig.labels?.[field] || titleCase(field);

    const placeholder = value
      ? ""
      : `Enter ${label.toLowerCase()}`;

    switch (type) {
      case "boolean":
        return (
          <Input
            type="select"
            value={
              value === "t" || value === "true" || value === true ? "true" :
                value === "f" || value === "false" || value === false ? "false" :
                  ""
            }
            onChange={(e) =>
              onChange(e.target.value === "true" ? "true" : "false")
            }
          >
            <option value="">Select...</option>
            <option value="true">True</option>
            <option value="false">False</option>
          </Input>
        );

      case "select":
        if (loadingOptions) return <Spinner animation="border" size="sm" />;

        const staticNormalized = (tableConfig.options?.[field] || []).map((opt) =>
          typeof opt === "object" ? opt : { label: opt, value: opt }
        );

        const dynamicNormalized = dynamicOptions[field] || [];

        // Merge & dedupe by value
        const merged = [...staticNormalized, ...dynamicNormalized].filter(
          (opt, index, arr) =>
            index === arr.findIndex((o) => o.value === opt.value)
        );

        // Ensure current value always exists
        if (value && !merged.some((opt) => opt.value === value)) {
          merged.push({ label: value, value });
        }

        return (
          <Input
            type="select"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="">Select...</option>
            {merged.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Input>
        );

      case "number":
        return (
          <Input
            type="number"
            value={value ?? ""}
            onChange={(e) => onChange(Number(e.target.value))}
          />
        );


      case "readonly":
        return (
          <Input
            type="text"
            value={value ?? ""}
            readOnly
            className="bg-light cursor-default"
          />
        );

      case "textarea":
        return (
          <Input
            type="textarea"
            rows={10}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            style={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }}
          />
        );

      default:
        return (
          <Input
            type="text"
            value={value ?? ""}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)}
          />
        );
    }
  };

  if (loading) return <div className="text-center mt-5"><Spinner animation="border" /><p>Loading row data...</p></div>;

  return (
    <>
      <SimpleHeader name={`Edit ${singular}`} parentName="HUCS" />
      <Container className="mt--6">
        <Card className="shadow-sm" style={{ marginTop: "-3rem" }}>
          <Card.Header className="header-theme">
            <div className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0">EDIT {singular.toUpperCase()}</h4>

              {tableName === "v_extensions" && (
                <Button
                  variant="info"
                  onClick={() =>
                    navigate(
                      `/admin/hucs/extensionSettings?extension_uuid=${rowData.extension_uuid}`
                    )
                  }
                >
                  Settings
                </Button>
              )}

              {tableName === "v_domains" && (
                <Button
                  variant="info"
                  onClick={() =>
                    navigate(
                      `/admin/hucs/domainSettings?domain_uuid=${rowData.domain_uuid}`
                    )
                  }
                >
                  Settings
                </Button>
              )}
            </div>
          </Card.Header>

          <Card.Body className="p-4">
            {error && <Alert ref={errorRef} variant="danger">{error}</Alert>}

            {/* Advanced Settings Button */}
            {advancedSections.length > 0 && (
              <Button
                variant="info"
                className="mb-4"
                onClick={() => setShowAdvancedModal(true)}
              >
                ADVANCED SETTINGS
              </Button>
            )}

            {/* Basic Sections */}
            <Form
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSave();
                }
              }}
            >
              {/* If no sections, render all fields flat */}
              {!hasSections && (
                <Row>
                  {getAllFieldsWithoutSections().map((field) => (
                    <Col md={4} key={field}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          {formConfig.labels?.[field] ||
                            field.replace(/_/g, " ").replace(/\b\w/g, (l) =>
                              l.toUpperCase()
                            )}
                          {formConfig.required?.includes(field) && (
                            <span style={{ color: "red" }}> *</span>
                          )}
                        </Form.Label>

                        {renderField(
                          formConfig,
                          field,
                          rowData?.[field],
                          (val) => handleChange(field, val)
                        )}
                      </Form.Group>
                    </Col>
                  ))}
                </Row>
              )}

              {/* Sections */}
              {hasSections &&
                basicSections.map(([sectionName, fields]) => (
                  <div key={sectionName} className="mb-4">
                    <h5
                      className="section-title mb-3"
                      style={{ fontSize: "1.5rem", fontWeight: 700 }}
                    >
                      {sectionName}
                    </h5>
                    <Row>
                      {fields.map((f) => (
                        <Col md={4} key={f}>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              {formConfig.labels?.[f] ||
                                f.replace(/_/g, " ").replace(/\b\w/g, (l) =>
                                  l.toUpperCase()
                                )}
                              {formConfig.required?.includes(f) && (
                                <span style={{ color: "red" }}> *</span>
                              )}
                            </Form.Label>
                            {renderField(formConfig, f, rowData[f], (val) =>
                              handleChange(f, val)
                            )}
                          </Form.Group>
                        </Col>
                      ))}
                    </Row>
                  </div>
                ))}

              <div className="mt-4 d-flex justify-content-between">
                <Button variant="danger" onClick={handleBack}>
                  Cancel
                </Button>
                <Button variant="success" onClick={handleSave} disabled={!isDirty}>
                  Save
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>

      {/* Advanced Modal */}
      <Modal show={showAdvancedModal} onHide={handleAdvancedBack} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Advanced Options</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {advancedSections.map(([sectionName, sectionData]) => (
            <Card key={sectionName} className="mb-4">
              <Card.Header>
                <strong>{sectionName}</strong>
              </Card.Header>
              <Card.Body>
                <Row>
                  {sectionData.fields.map((f) => (
                    <Col md={4} key={f}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          {formConfig.labels?.[f] ||
                            f.replace(/_/g, " ").replace(/\b\w/g, (l) =>
                              l.toUpperCase()
                            )}
                          {formConfig.required?.includes(f) && (
                            <span style={{ color: "red" }}> *</span>
                          )}
                        </Form.Label>
                        {renderField(
                          formConfig,
                          f,
                          advancedData[f],
                          (val) => handleAdvancedFieldChange(f, val)
                        )}
                      </Form.Group>
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>
          ))}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="danger" onClick={handleAdvancedBack}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleAdvancedSave}
            disabled={
              !Object.keys(advancedData || {}).some((key) =>
                isFieldDirty(advancedData[key], advancedInitial.current?.[key])
              )
            }
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default GenericEdit;
